import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import Typography from '@mui/material/Typography';

// Icons
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

import ToolPanel from '../components/ToolPanel';
import TipsWindow from '../components/TipsWindow';
import { useTip } from '../hooks/useTips';
import {
  BUTTON_STYLES,
  LIST_STYLES,
  TYPOGRAPHY_STYLES,
  SECTION_STYLES,
  ROW_STYLES,
} from '../styles/viewerStyles';

function ViewpointsTools({
  viewpoints,
  highlightedViewpoint,
  saveCurrentViewpoint,
  handleViewpointsFileImport,
  clearAllViewpoints,
  exportViewpoints,
  restoreViewpoint,
  deleteViewpoint,
  highlightPyramid,
  clearPyramidHighlight,
  toggleViewpointsPanel,
  DRAWER_WIDTH
}) {
  const { t } = useTranslation();

  // Tips integration - component is only mounted when panel is open
  const { isOpen: isTipOpen, title: tipTitle, markdownBody: tipBody, close: closeTip } = useTip('viewpoints', true);

  return (
    <>
    <TipsWindow
      isOpen={isTipOpen}
      title={tipTitle}
      markdownBody={tipBody}
      onClose={({ doNotShowAgain }) => closeTip(doNotShowAgain)}
    />
    <ToolPanel
      title={t('Viewpoints') || 'Viewpoints'}
      onClose={toggleViewpointsPanel}
      width={DRAWER_WIDTH}
    >
      {/* Action Buttons */}
      <Box sx={ROW_STYLES.buttonRow}>
        {/* Save Current Viewpoint */}
        <ToggleButton
          value="save"
          selected={false}
          onChange={() => saveCurrentViewpoint()}
          size="small"
          sx={BUTTON_STYLES.iconButton}
          title={t('SaveCurrentViewpoint') || 'Save Current Viewpoint'}
        >
          <BookmarkBorderIcon />
        </ToggleButton>

        {/* Import Viewpoints */}
        <ToggleButton
          value="import"
          selected={false}
          component="label"
          size="small"
          sx={BUTTON_STYLES.iconButton}
          title={t('ImportViewpoints') || 'Import Viewpoints'}
        >
          <UploadIcon />
          <input
            type="file"
            accept=".json"
            onChange={handleViewpointsFileImport}
            style={{ display: 'none' }}
          />
        </ToggleButton>

        {/* Clear All Viewpoints */}
        <ToggleButton
          value="clear"
          selected={false}
          onChange={clearAllViewpoints}
          size="small"
          sx={BUTTON_STYLES.deleteButton}
          title={t('ClearAllViewpoints') || 'Clear All Viewpoints'}
        >
          <DeleteOutlineIcon />
        </ToggleButton>
      </Box>

      {/* Viewpoints List */}
      {viewpoints.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" sx={TYPOGRAPHY_STYLES.caption}>
              {t('SavedViewpoints') || 'Saved Viewpoints'} ({viewpoints.length})
            </Typography>
            <IconButton
              onClick={exportViewpoints}
              sx={BUTTON_STYLES.smallIconButton(true)}
              title={t('ExportViewpoints') || 'Export Viewpoints'}
            >
              <SaveIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          </Box>

          <Box sx={LIST_STYLES.scrollContainer('400px')}>
            {viewpoints.map((viewpoint) => {
              const isHighlighted = highlightedViewpoint === viewpoint.id;
              return (
                <Box
                  key={viewpoint.id}
                  sx={LIST_STYLES.item(isHighlighted)}
                  onClick={(e) => {
                    if (e.target.closest('button')) return;
                    if (isHighlighted) {
                      clearPyramidHighlight();
                    } else {
                      highlightPyramid(viewpoint.id);
                    }
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{
                      pl: 1,
                      color: isHighlighted ? '#000' : '#fff',
                      fontSize: '0.7rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'block',
                      fontWeight: isHighlighted ? 'bold' : 'normal'
                    }}>
                      {viewpoint.name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => restoreViewpoint(viewpoint)}
                      sx={BUTTON_STYLES.smallIconButton(true, isHighlighted)}
                      title={t('RestoreViewpoint') || 'Restore Viewpoint'}
                    >
                      <BookmarkIcon sx={{ fontSize: '12px' }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => deleteViewpoint(viewpoint.id)}
                      sx={BUTTON_STYLES.smallDeleteButton(isHighlighted)}
                      title={t('DeleteViewpoint') || 'Delete Viewpoint'}
                    >
                      <CloseIcon sx={{ fontSize: '12px' }} />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </ToolPanel>
    </>
  );
}

ViewpointsTools.propTypes = {
  viewpoints: PropTypes.array.isRequired,
  highlightedViewpoint: PropTypes.string,
  saveCurrentViewpoint: PropTypes.func.isRequired,
  handleViewpointsFileImport: PropTypes.func.isRequired,
  clearAllViewpoints: PropTypes.func.isRequired,
  exportViewpoints: PropTypes.func.isRequired,
  restoreViewpoint: PropTypes.func.isRequired,
  deleteViewpoint: PropTypes.func.isRequired,
  highlightPyramid: PropTypes.func.isRequired,
  clearPyramidHighlight: PropTypes.func.isRequired,
  toggleViewpointsPanel: PropTypes.func.isRequired,
  DRAWER_WIDTH: PropTypes.number.isRequired
};

export default ViewpointsTools;
